import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import type { RootState } from "../../store";
import type { Activity, Project, ProjectArea } from "../../../types/interfaces";
import type { CachedLockInfo } from "./projectTypes";
import { Feature, FeatureCollection } from "@components/actions/map/mapMessages";

//missing carbon balance and notes
const initialState: ProjectState = {
	project: undefined,
	activity: undefined,
	activeActivityId: null,
	showFinalized: false,
	showTrashbin: false,
	tags: [],
	temporaryMapData: null,
	lockInfo: null,
	projectAreaDefined: null,
	projectAreaStatusVersion: 0,
	projectArea: null,
	projectAreaId: null,
};

const projectSlice = createSlice({
	name: "project",
	initialState: initialState,
	reducers: {
		setProject: (state, action: PayloadAction<Project | null>) => {
			state.project = action.payload;
		},
		setProjectArea: (state, action: PayloadAction<ProjectArea | null>) => {
			if (state.project) {
				state.project = { ...state.project, area: action.payload }
			}
		},

		setActivity: (state, action: PayloadAction<Activity>) => {
			state.activity = action.payload;
			state.activeActivityId = action.payload.id;
		},
		clearProject: (state) => {
			state.project = null;
			state.activity = null;
			state.activeActivityId = null;
			state.projectAreaDefined = null;
			state.projectAreaStatusVersion = 0;
			state.projectArea = null;
			state.projectAreaId = null;
			state.lockInfo = null;
		},
		invalidateProjectAreaStatus: (state) => {
			state.projectAreaDefined = null;
			state.projectArea = null;
			state.projectAreaId = null;
			state.projectAreaStatusVersion++;
		},
		setProjectAreaDefined: (state, action: PayloadAction<{ defined: boolean | null, area: Feature | null, id: string | null }>) => {
			state.projectAreaDefined = action.payload.defined;
			state.projectArea = action.payload.area;
			state.projectAreaId = action.payload.id;
		},
		setShowFinalized: (state, action: PayloadAction<boolean>) => {
			state.showFinalized = action.payload;
		}, 
		setShowTrashbin: (state, action: PayloadAction<boolean>) => {
			state.showTrashbin = action.payload;
		},
		setTags: (state, action: PayloadAction<number[]>) => {
			state.tags = action.payload;
		},
		setTemporaryMapData: (state, action: PayloadAction<FeatureCollection | null>) => { state.temporaryMapData = action.payload },
		clearTemporaryMapData: (state) => { state.temporaryMapData = null },
		setLockInfo: (state, action: PayloadAction<CachedLockInfo>) => {
			state.lockInfo = action.payload;
		},
		clearLockInfo: (state) => {
			state.lockInfo = null;
		}
	},
});

export const { setProject, setActivity, clearProject, setProjectArea, setShowFinalized, setTags, setShowTrashbin, setTemporaryMapData, clearTemporaryMapData, setLockInfo, clearLockInfo, setProjectAreaDefined, invalidateProjectAreaStatus } =
	projectSlice.actions;
export const selectCurrentProject = (state: RootState) => state.project;
export const selectCurrentActiveActivity = (state: RootState) =>
	state.project.activeActivityId;
export const selectProjectsFilters = (state: RootState) => ({
	showFinalized: state.project.showFinalized,
	showTrashbin: state.project.showTrashbin,
	tags: state.project.tags,
});
export const selectTemporaryMapData = (state: RootState) => state.project.temporaryMapData;
export const selectLockInfo = (state: RootState) => state.project.lockInfo;
export const selectProjectAreaDefined = (state: RootState) => state.project.projectAreaDefined;
export const selectProjectAreaStatusVersion = (state: RootState) => state.project.projectAreaStatusVersion;
export const selectProjectArea = (state: RootState) => state.project.projectArea;
export const selectProjectAreaId = (state: RootState) => state.project.projectAreaId;

export default projectSlice.reducer;

interface ProjectState {
	project?: Project | null;
	activeActivityId?: number | null;
	activity?: Activity | null;
	activities?: Activity[],
	showFinalized: boolean,
	showTrashbin: boolean,
	tags: number[],
	temporaryMapData: FeatureCollection | null,
	lockInfo: CachedLockInfo | null,
	projectAreaDefined: boolean | null,
	projectAreaStatusVersion: number,
	projectArea: Feature | null,	
	projectAreaId: string | null,
}
